import React from 'react';
import {Button, ButtonGroup, Dropdown, Spinner} from '@goproblems/ui';
import {useHotkeys} from 'react-hotkeys-hook';
import {useLocalStorageState} from '@utils';
import {useTranslation} from 'react-i18next';

type MobilePotdNextAction = 'difficulty' | 'potd';

const DEFAULT_STORAGE_KEY = 'mobile-potd-next-action';

type MobilePotdNextButtonProps = {
    disabled: boolean;
    storageKey?: string;
    difficultyActionLabel: React.ReactNode;
    potdActionLabel: React.ReactNode;
    onDifficultyClick: () => Promise<void>;
    onPotdClick: () => Promise<void>;
};

export const MobilePotdNextButton: React.FC<MobilePotdNextButtonProps> = ({
    disabled,
    storageKey = DEFAULT_STORAGE_KEY,
    difficultyActionLabel,
    potdActionLabel,
    onDifficultyClick,
    onPotdClick,
}) => {
    const {t} = useTranslation();
    const [storedAction, setStoredAction] = useLocalStorageState<string>(
        storageKey,
        'difficulty'
    );
    const selectedAction: MobilePotdNextAction =
        storedAction === 'potd' ? 'potd' : 'difficulty';

    const currentAction =
        selectedAction === 'potd' ? onPotdClick : onDifficultyClick;

    useHotkeys(
        ['Option+n', 'Alt+n'],
        () => {
            void currentAction();
        },
        {preventDefault: true}
    );

    const handleSelectAction = (action: MobilePotdNextAction) => {
        setStoredAction(action);
    };

    const currentLabel =
        selectedAction === 'potd' ? potdActionLabel : difficultyActionLabel;

    return (
        <Dropdown
            as={ButtonGroup}
            className="mobile-potd-next-button relative z-20">
            <Button
                variant="success"
                className="btn min-w-[60px] pr-1"
                disabled={disabled}
                onClick={() => {
                    void currentAction();
                }}>
                {disabled ? <Spinner size="sm" /> : currentLabel}
            </Button>
            <Dropdown.Toggle
                split
                variant="success"
                className="px-2"
                id="next-problem-mobile-potd-dropdown"
                disabled={disabled}>
                <span className="visually-hidden">
                    {t('common.toggle_next_action')}
                </span>
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
                <Dropdown.Item
                    active={selectedAction === 'difficulty'}
                    onClick={() => handleSelectAction('difficulty')}>
                    {difficultyActionLabel}
                </Dropdown.Item>
                <Dropdown.Item
                    active={selectedAction === 'potd'}
                    onClick={() => handleSelectAction('potd')}>
                    {potdActionLabel}
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );
};
